import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  FREEZE_FOR_CAPTURE,
  MotionReveal,
  itemVariants,
  replayingViewport,
  useSectionSnap,
} from '@components/motion/scroll-motion';
import { AppleLogo, GooglePlayLogo } from '@phosphor-icons/react';
import { useMediaAsset } from '@hooks/use-media-asset';
import { NewsletterOverlay } from '@components/landing/newsletter-overlay';
import { storeCtaProps } from '@components/landing/store-cta';
import { WeeStory } from '@components/wee/wee-section';
import { useWaterStory } from '@components/water/water-story';
import { SectionDots } from '@components/landing/section-dots';
import { ResultStory } from '@components/landing/result-section';
import { HowWeBuild } from '@components/landing/how-section';
import { ProductDemos } from '@components/landing/demo-section';
import { GettingStarted } from '@components/landing/getting-started';
import { DownloadPanel } from '@components/landing/download-panel';
import { AutoplayControl, NextSectionControl } from '@components/landing/autoplay-control';
import { SiteNav } from '@components/landing/site-nav';
import { SiteFooter } from '@components/landing/site-footer';
import { HeroIntro, HeroReveal, CUE as HERO_CUE } from '@components/landing/hero-intro';
import { useAutoplay, type AutoplayStep } from '@components/landing/use-autoplay';
import { trackCtaClick } from '@logging';

/** Where a newsletter-overlay open came from. Closed, for the same reason
 *  `StorePlacement` is: `placement` is a dashboard GROUP BY, and a typo
 *  would quietly split one row in two rather than fail. The footer's
 *  Follow ('footer') opens its own, in `SiteFooter`. */
type NewsletterPlacement = 'blog_card';
import {
  BentoSection,
  BentoContainer,
  BentoEyebrow,
  BentoHeading,
} from '@components/landing/bento.styles';
import {
  PageRoot,
  HeroLayout,
  HeroCopy,
  HeroHeadline,
  HeroSubheadline,
  HeroCtaRow,
  TestimonialMeta,
  TestimonialName,
  TestimonialRole,
  LoveWall,
  LoveDecorationQuote,
  LoveCard,
  LoveBigQuote,
  LoveSmallQuote,
  LovePullQuote,
  LoveAvatarRow,
  LoveAvatarStack,
  LoveAvatarCopy,
  BlogHead,
  BlogGrid,
  BlogLink,
  BlogCardInner,
  BlogThumb,
  BlogBody,
  BlogEyebrow,
  BlogTitle,
  BlogBlurb,
  BlogMeta,
  SeeAllLink,
  FooterCta,
} from './landing-page.styles';

// ============================================================================
// Data
// ============================================================================

const TESTIMONIALS = [
  {
    quote: "The morning briefing is the first thing I read every day. I'm not the bottleneck anymore.",
    name: '[Mom name]',
    role: 'Mom of two, full-time work-from-home',
  },
  {
    quote: "It's like having a really organized co-parent who never forgets anything.",
    name: '[Mom name]',
    role: 'Mom of three, dog owner',
  },
  {
    quote: "I cried a little the first time it reminded me about the dog's heartworm and the kids' dental cleanings in the same week.",
    name: '[Mom name]',
    role: 'Mom of one, two pets',
  },
];

// ============================================================================
// Page
// ============================================================================

/** The sections in page order, for the page dots: one per section that
 *  mounts, so it tracks the snap indices (the hidden User Love section is
 *  in neither). */
const SECTION_NAMES = [
  'the top',
  'Wish, Explore, Enact',
  'The result',
  'How we build',
  'Demos',
  'Getting started',
  'Download',
  'the blog',
] as const;

const WEE_SECTION = 1;
const HOW_SECTION = 3;
const DEMO_SECTION = 4;
const GETTING_STARTED_SECTION = 5;
const DOWNLOAD_SECTION = 6;
const BLOG_SECTION = 7;

/**
 * The page's autoplay rhythm, one step per section above. The hero holds
 * while its lines arrive and a little after; the WEE section plays its
 * story until Enact has finished; the rest hold long enough to read. It
 * ends at Download, which has no step (June, 2026-09-23): the page rests
 * there, and See Blogs takes the reader on.
 */
const AUTOPLAY_STEPS: readonly AutoplayStep[] = [
  // The hero's words arrive from 2.0s to 3.6s (hero-intro's CUE); the page
  // moves on at 4.75s, half the 9.5s it held before (June, 2026-09-23:
  // "too long, cut it in half").
  { dwellMs: 4750 },
  { untilStoryEnds: true, maxMs: 30000 },
  { dwellMs: 9000 },
  // How we build: its story runs 68s — the first mom, the night the
  // assistant learns, the next mom, the three levels — and moves the page
  // on when it ends; this is the fallback.
  { untilStoryEnds: true, maxMs: 76000 },
  // Demos: the row turns through its wishes, each as long as its words take
  // to read, and the app card: ~52s a turn.
  { untilStoryEnds: true, maxMs: 65000 },
  // Getting started: three steps to read.
  { dwellMs: 12000 },
];

export const LandingPage: React.FC = () => {
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  /* Hero scroll progress — drives the hero's lift. Starts at 0 when the
     hero is fully in view at page-top, hits 1 once the hero has scrolled
     out the top, so the drift is tied to "leaving the hero" rather than to
     the page's overall scroll. */
  const heroRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  /* Section refs for the scroll-end auto-snap. Index 0 = hero; subsequent
     indices populate as each BentoSection mounts. */
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const setSectionRef = (i: number) => (el: HTMLElement | null) => {
    sectionRefs.current[i] = el;
    if (i === 0) heroRef.current = el;
  };

  /* The page is a story told from its top, so every visit opens on the
     hero. Left to itself the browser puts a reload back wherever the
     reader, or the autoplay, had got to: the arrival then plays out
     off-screen above them, and the autoplay — which always starts at the
     hero's step — scrolls them back up to "What" when it moves on. A
     link to a section (#download) still lands on that section. */
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    if (!window.location.hash) window.scrollTo(0, 0);
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useSectionSnap(sectionRefs);
  const reduced = useReducedMotion() ?? false;
  const autoplay = useAutoplay(sectionRefs, AUTOPLAY_STEPS, !reduced && !FREEZE_FOR_CAPTURE, reduced);
  const heroCopyY = useTransform(heroProgress, [0, 1], [0, -80]);

  /* No landing-specific view event: `web_page_viewed` already fires for
     every route including this one, and a second event for the same view
     would have to be excluded by hand in every dashboard query. */

  // The page dot to light: the last section whose top has crossed 40% of
  // the way down the screen. (The nav turns opaque by itself, `SiteNav`.)
  useEffect(() => {
    const onScroll = () => {
      const line = window.innerHeight * 0.4;
      let current = 0;
      sectionRefs.current.forEach((el, i) => {
        if (el && el.getBoundingClientRect().top <= line) current = i;
      });
      setCurrentSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* While the page plays itself the water is telling that story, so it
     ignores the pointer; when the reader takes over, it answers them. */
  const water = useWaterStory();
  useEffect(() => {
    const shared = water.current;
    shared.interactive = !autoplay.playing;
    return () => {
      shared.interactive = true;
    };
  }, [autoplay.playing, water]);


  /* Four surfaces open the same overlay, and only its submit was reported —
     so the open-to-submit conversion, which is the only number that says
     whether the overlay works, could not be computed at all. Placement is
     carried per call site because "the footer Follow button converts at half
     the rate of the prompt card" is the actionable version of that. */
  const openNewsletter = (placement: NewsletterPlacement) => {
    trackCtaClick('newsletter_open', placement);
    setNewsletterOpen(true);
  };

  return (
    <PageRoot>
      {/* The floating nav; it tells the page how far down it reaches
          (--nav-foot), so each section's words start below it. */}
      <SiteNav solidAfter={0.85} />

      {/* Hero — the mark arrives first and opens like a star, and the
          words step out of that light (`hero-intro.tsx` holds the clock;
          the hero is in view at load, so whileInView never fires here).
          Then a scroll-linked lift drifts the words up as you scroll. */}
      <BentoSection ref={setSectionRef(0)} $variant="hero">
        <BentoContainer>
          <HeroLayout>
            {/* Outside the copy's scroll-linked lift: the fog is fixed to
                the window, and a transformed ancestor would trap it. */}
            <HeroIntro />
            <motion.div style={{ y: heroCopyY }}>
              <HeroCopy>
                <HeroReveal at={HERO_CUE.headline}>
                  <HeroHeadline>The Home CEO</HeroHeadline>
                </HeroReveal>
                <HeroReveal at={HERO_CUE.tagline}>
                  <HeroSubheadline>Specialized AI for managing your household</HeroSubheadline>
                </HeroReveal>
                <HeroReveal at={HERO_CUE.ctas}>
                  <HeroCtaRow>
                    <FooterCta as="a" {...storeCtaProps('ios', 'hero')}>
                      <AppleLogo weight="fill" /> App Store
                    </FooterCta>
                    <FooterCta as="a" {...storeCtaProps('android', 'hero')}>
                      <GooglePlayLogo weight="fill" /> Google Play
                    </FooterCta>
                  </HeroCtaRow>
                </HeroReveal>
              </HeroCopy>
            </motion.div>
          </HeroLayout>
        </BentoContainer>
      </BentoSection>

      {/* Section 2: Wish → Explore → Enact — the app's home story, one
          card per beat on the app's own clock, with the page's water
          telling it too while the section fills the screen. */}
      <BentoSection ref={setSectionRef(1)}>
        <BentoContainer>
          <WeeStory onCycleEnd={() => autoplay.storyEnded(WEE_SECTION)} />
        </BentoContainer>
      </BentoSection>

      {/* Section 3: Result — the page stays in Enact's glow: the water
          turns to a slow swirl of the sunrise, and the house keeps coming
          alive. */}
      <BentoSection ref={setSectionRef(2)}>
        <BentoContainer>
          <ResultStory />
        </BentoContainer>
      </BentoSection>

      {/* Section 4: How we build — every wish makes the next answer better. */}
      <BentoSection ref={setSectionRef(3)}>
        <BentoContainer>
          <HowWeBuild
            onCycleEnd={() => autoplay.storyEnded(HOW_SECTION)}
            onSteer={() => autoplay.storySteered(HOW_SECTION)}
          />
        </BentoContainer>
      </BentoSection>

      {/* Section 5: Product demos — three wishes and the answers they became. */}
      <BentoSection ref={setSectionRef(4)}>
        <BentoContainer>
          <ProductDemos onCycleEnd={() => autoplay.storyEnded(DEMO_SECTION)} pagePlaying={autoplay.playing} />
        </BentoContainer>
      </BentoSection>

      {/* Section 6: Getting started — the app's three sign-up steps as it
          draws them (the household, the goals, the account), and what each
          answer buys. */}
      <BentoSection ref={setSectionRef(5)}>
        <BentoContainer>
          <GettingStarted
            playing={autoplay.playing && autoplay.index === GETTING_STARTED_SECTION}
            onGetApp={() => sectionRefs.current[DOWNLOAD_SECTION]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
        </BentoContainer>
      </BentoSection>

      {/* User Love — placeholder testimonials. Hidden until real user
          quotes land. Re-enable by removing the `false &&` guard below AND
          incrementing the trailing setSectionRef indices on Download Now
          (6 → 7) and Blog (7 → 8). */}
      {false && (
      <BentoSection ref={setSectionRef(6)}>
        <BentoContainer>
          <MotionReveal>
            <BentoEyebrow>User Love</BentoEyebrow>
            <BentoHeading>A wall of moms saying it&rsquo;s working.</BentoHeading>
          </MotionReveal>
          <LoveWall
            as={motion.div}
            initial="hidden"
            whileInView="show"
            viewport={replayingViewport}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
          >
            <LoveDecorationQuote aria-hidden>&ldquo;</LoveDecorationQuote>

            {/* Big featured — gets its own scale-in for emphasis. */}
            <LoveCard
              as={motion.div}
              variants={{
                hidden: { opacity: 0, scale: 0.95, y: 24 },
                show: { opacity: 1, scale: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
              }}
              $cols={7}
              $rows={2}
              $colsMd={12}
              $rowsMd={1}
              $tone="solid"
            >
              <LoveBigQuote>&ldquo;{TESTIMONIALS[2].quote}&rdquo;</LoveBigQuote>
              <TestimonialMeta>
                <TestimonialName>{TESTIMONIALS[2].name}</TestimonialName>
                <TestimonialRole>{TESTIMONIALS[2].role}</TestimonialRole>
              </TestimonialMeta>
            </LoveCard>

            <LoveCard as={motion.div} variants={itemVariants} $cols={5} $colsMd={6} $tone="glass">
              <LoveSmallQuote>&ldquo;{TESTIMONIALS[0].quote}&rdquo;</LoveSmallQuote>
              <TestimonialMeta>
                <TestimonialName>{TESTIMONIALS[0].name}</TestimonialName>
                <TestimonialRole>{TESTIMONIALS[0].role}</TestimonialRole>
              </TestimonialMeta>
            </LoveCard>

            <LoveCard as={motion.div} variants={itemVariants} $cols={5} $colsMd={6} $tone="accent" $align="left">
              <LovePullQuote>&ldquo;Like a really organized co-parent.&rdquo;</LovePullQuote>
            </LoveCard>

            <LoveCard as={motion.div} variants={itemVariants} $cols={4} $colsMd={6} $tone="glass">
              <LoveSmallQuote>&ldquo;{TESTIMONIALS[1].quote}&rdquo;</LoveSmallQuote>
              <TestimonialMeta>
                <TestimonialName>{TESTIMONIALS[1].name}</TestimonialName>
                <TestimonialRole>{TESTIMONIALS[1].role}</TestimonialRole>
              </TestimonialMeta>
            </LoveCard>

            <LoveCard as={motion.div} variants={itemVariants} $cols={4} $colsMd={6} $tone="solid" $align="left">
              <LovePullQuote>&ldquo;I&rsquo;m not the bottleneck anymore.&rdquo;</LovePullQuote>
            </LoveCard>

            <LoveCard as={motion.div} variants={itemVariants} $cols={4} $colsMd={12} $tone="glass">
              <LoveAvatarRow>
                <LoveAvatarStack>
                  <span>SM</span>
                  <span>DK</span>
                  <span>PT</span>
                  <span>JL</span>
                  <span>+</span>
                </LoveAvatarStack>
                <LoveAvatarCopy>
                  <strong>Loved by busy households</strong>
                  Real moms, real schedules, real wins.
                </LoveAvatarCopy>
              </LoveAvatarRow>
            </LoveCard>
          </LoveWall>
        </BentoContainer>
      </BentoSection>
      )}

      {/* Section 7: Download — one calm panel with both stores: a single
          action needs no more than that. */}
      <BentoSection ref={setSectionRef(6)}>
        <BentoContainer>
          <DownloadPanel />
        </BentoContainer>
      </BentoSection>

      {/* Section 8: Blog — heading reveals as a block, then the post
          cards stagger in left-to-right. */}
      <BentoSection ref={setSectionRef(7)}>
        <BentoContainer>
          <MotionReveal>
            <BlogHead>
              <BentoEyebrow>From our blog</BentoEyebrow>
              <BentoHeading>Learn from Lessons on the Journey.</BentoHeading>
            </BlogHead>
          </MotionReveal>
          <BlogGrid
            as={motion.div}
            initial="hidden"
            whileInView="show"
            viewport={replayingViewport}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } } }}
          >
            <motion.div variants={itemVariants}>
              <BlogPostCard slug="ai-uneven-impact" thumbAsset="ai_uneven_impact_dark_blog">
                <BlogEyebrow>Article · Apr 2026</BlogEyebrow>
                <BlogTitle>AI&rsquo;s Uneven Impact</BlogTitle>
                <BlogBlurb>
                  Why some households are pulling ahead with AI, and what calm, structured tooling looks like for everyone else.
                </BlogBlurb>
                <BlogMeta>5 min read</BlogMeta>
              </BlogPostCard>
            </motion.div>

            <motion.div variants={itemVariants}>
              <BlogPostCard tone="highlight" onClick={() => openNewsletter('blog_card')}>
                <BlogEyebrow style={{ color: 'rgba(255,255,255,0.85)' }}>
                  Get notified
                </BlogEyebrow>
                <BlogTitle style={{ color: 'white', fontSize: '1.5rem' }}>
                  New posts straight to your inbox.
                </BlogTitle>
                <BlogBlurb style={{ color: 'rgba(255,255,255,0.85)' }}>
                  Short, calm, and actually useful. No filler.
                </BlogBlurb>
                <BlogMeta style={{ color: 'rgba(255,255,255,0.85)' }}>
                  Subscribe →
                </BlogMeta>
              </BlogPostCard>
            </motion.div>

            {/* Placeholder card — uncomment by removing the `false &&`
                guard once a real post for this slot is ready. */}
            {false && (
            <motion.div variants={itemVariants}>
              <BlogPostCard tone="solid" thumbAsset="family_planner_hero">
                <BlogEyebrow>Coming soon</BlogEyebrow>
                <BlogTitle>The Calm-Household Operating System</BlogTitle>
                <BlogBlurb>
                  A deeper look at how families run when admin is automated and the morning briefing is the source of truth.
                </BlogBlurb>
                <BlogMeta>—</BlogMeta>
              </BlogPostCard>
            </motion.div>
            )}
          </BlogGrid>
          <SeeAllLink to="/ai-blogs">See all posts →</SeeAllLink>
        </BentoContainer>
      </BentoSection>

      <SiteFooter />

      {newsletterOpen && <NewsletterOverlay onClose={() => setNewsletterOpen(false)} />}

      <SectionDots
        labels={SECTION_NAMES}
        current={currentSection}
        onSelect={(i) => sectionRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />

      {!FREEZE_FOR_CAPTURE && currentSection < DOWNLOAD_SECTION && (
        <AutoplayControl playing={autoplay.playing} onToggle={autoplay.toggle} />
      )}
      {/* Where the rhythm ends, the control's place leads on to the blog;
          on the blog itself, nothing. */}
      {!FREEZE_FOR_CAPTURE && currentSection === DOWNLOAD_SECTION && (
        <NextSectionControl
          label="See Blogs"
          onGo={() => sectionRefs.current[BLOG_SECTION]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        />
      )}
    </PageRoot>
  );
};

// ============================================================================
// Blog post card — thumbnail or highlight tone
// ============================================================================

interface BlogPostCardProps {
  slug?: string;
  thumbAsset?: string;
  tone?: 'image' | 'highlight' | 'solid';
  onClick?: () => void;
  children: React.ReactNode;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({
  slug,
  thumbAsset,
  tone,
  onClick,
  children,
}) => {
  const { url } = useMediaAsset(thumbAsset ?? null);
  const isHighlight = tone === 'highlight';

  const inner = (
    <BlogCardInner $tone={isHighlight ? 'highlight' : 'image'}>
      {!isHighlight && <BlogThumb $url={url ?? undefined} />}
      <BlogBody>{children}</BlogBody>
    </BlogCardInner>
  );

  if (slug) {
    return <BlogLink to={`/ai-blogs/${slug}`}>{inner}</BlogLink>;
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          textAlign: 'left',
          cursor: 'pointer',
          width: '100%',
          height: '100%',
        }}
      >
        {inner}
      </button>
    );
  }
  return <div>{inner}</div>;
};
